import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, from, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

export type CadDrawingContextType = 'standalone' | 'gis' | 'project' | 'lrs_cadastral';

export type CadConversionStatus = 'pending' | 'ready' | 'failed' | 'not_needed';

export interface CadDrawing {
  id: number;
  org_id: number | null;
  project_id: number | null;
  context_type: CadDrawingContextType;
  context_id: string | null;
  original_name: string;
  file_url: string;
  file_size: number | null;
  format: string;
  conversion_status?: CadConversionStatus;
  conversion_error?: string | null;
  uploaded_by: number | null;
  created_at: string;
  /** Overrides the default `cad/drawings/:id/dxf` source (e.g. Document Manager files). */
  dxf_url?: string;
}

export const CAD_FILE_EXTENSIONS = ['dwg', 'dxf'];

export function isCadFile(extensionOrName: string | null | undefined): boolean {
  const ext = String(extensionOrName || '').toLowerCase().split('.').pop() || '';
  return CAD_FILE_EXTENSIONS.includes(ext);
}

/** Adapts a Document Manager record so it can be opened in the CAD viewer. */
export function documentToCadDrawing(doc: {
  id: number;
  document_name?: string;
  file_name: string;
  file_size?: number | null;
  file_extension?: string | null;
  project_id?: number | null;
  org_id?: number | null;
  uploaded_by?: number | null;
  created_at?: string;
}): CadDrawing {
  const format = String(doc.file_extension || doc.file_name || '').toLowerCase().split('.').pop() || '';
  return {
    id: doc.id,
    org_id: doc.org_id ?? null,
    project_id: doc.project_id ?? null,
    context_type: 'project',
    context_id: doc.project_id != null ? String(doc.project_id) : null,
    original_name: doc.file_name || doc.document_name || `drawing.${format}`,
    file_url: '',
    file_size: doc.file_size ?? null,
    format,
    uploaded_by: doc.uploaded_by ?? null,
    created_at: doc.created_at || '',
    dxf_url: `${environment.BASE_URL}documents/${doc.id}/dxf`,
  };
}

export interface CadDrawingContext {
  contextType: CadDrawingContextType;
  contextId?: string | number | null;
  projectId?: number | null;
}

export interface CadConverterHealth {
  reachable: boolean;
  dwgSupported: boolean;
  converterVersion: string | null;
}

export type CadDxfErrorCode = 'CONVERTER_UNAVAILABLE' | 'CONVERSION_FAILED' | 'NOT_FOUND' | 'NETWORK' | 'UNKNOWN';

export class CadDxfError extends Error {
  constructor(message: string, readonly code: CadDxfErrorCode) {
    super(message);
  }
}

/** Fonts used for drawing text, tried in order per glyph (opentype.js: TTF/OTF/WOFF only). */
export const CAD_FONT_URLS = [
  'assets/fonts/cad/roboto-latin-400-normal.woff',
  'assets/fonts/cad/roboto-latin-ext-400-normal.woff',
  'assets/fonts/cad/roboto-cyrillic-400-normal.woff',
  'assets/fonts/cad/roboto-greek-400-normal.woff',
  'assets/fonts/cad/roboto-symbols-400-normal.woff',
];

const HEALTH_TTL_MS = 60_000;

@Injectable({ providedIn: 'root' })
export class CadViewerService {
  private readonly api = environment.BASE_URL;
  private health$: Observable<CadConverterHealth> | null = null;
  private healthCheckedAt = 0;

  constructor(private http: HttpClient) {}

  /** Whether the server can convert DWG (LibreDWG installed). Cached for a minute. */
  health(): Observable<CadConverterHealth> {
    if (!this.health$ || Date.now() - this.healthCheckedAt > HEALTH_TTL_MS) {
      this.healthCheckedAt = Date.now();
      this.health$ = this.http.get<any>(`${this.api}cad/health`).pipe(
        map((res) => ({
          reachable: true,
          dwgSupported: !!res?.dwgSupported,
          converterVersion: res?.converterVersion ?? null,
        })),
        catchError(() => of({ reachable: false, dwgSupported: false, converterVersion: null })),
        shareReplay(1)
      );
    }
    return this.health$;
  }

  /**
   * Download the drawing as DXF (DWG is converted server-side) and return an
   * object URL. The caller must `URL.revokeObjectURL()` it when done.
   */
  fetchDxf(drawing: CadDrawing): Observable<string> {
    const url = drawing.dxf_url || `${this.api}cad/drawings/${drawing.id}/dxf`;
    return this.http.get(url, { responseType: 'blob' }).pipe(
      map((blob) => URL.createObjectURL(blob)),
      catchError((err: HttpErrorResponse) => from(this.readErrorMessage(err)).pipe(
        switchMap((message) => throwError(() => this.toDxfError(err.status, message)))
      ))
    );
  }

  private async readErrorMessage(err: HttpErrorResponse): Promise<string | null> {
    try {
      const body = err.error instanceof Blob ? JSON.parse(await err.error.text()) : err.error;
      return body?.error || body?.message || null;
    } catch {
      return null;
    }
  }

  private toDxfError(status: number, message: string | null): CadDxfError {
    switch (status) {
      case 503:
        return new CadDxfError(
          message || 'DWG conversion is not installed on the server. Upload a DXF or download the original.',
          'CONVERTER_UNAVAILABLE'
        );
      case 422:
      case 504:
        return new CadDxfError(message || 'The drawing could not be converted.', 'CONVERSION_FAILED');
      case 404:
        return new CadDxfError(message || 'Drawing not found.', 'NOT_FOUND');
      case 0:
        return new CadDxfError('The server could not be reached. Check your connection and try again.', 'NETWORK');
      default:
        return new CadDxfError(message || 'The drawing could not be loaded.', 'UNKNOWN');
    }
  }
}
